import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { toast } from 'react-toastify';

const BaseAssignmentModal = ({
  open,
  onClose,
  onAssign,
  loading,
  assignedItems = [],
  fetchItems,
  title,
  itemNameKey = 'name',
  itemIdKey = '_id',
  singleSelect = false,
  icon: Icon = null,
}) => {
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadItems();
    } else {
      // Reset selection when modal closes
      setSelectedItems([]);
    }
  }, [open]);

  useEffect(() => {
    // Pre-select assigned items when items are loaded
    if (items.length > 0 && assignedItems.length > 0) {
      const assignedItemObjects = items.filter(item =>
        assignedItems.some(assignedId => assignedId === item[itemIdKey])
      );
      setSelectedItems(assignedItemObjects);
    }
  }, [items, assignedItems]);

  const loadItems = async () => {
    setIsLoading(true);
    try {
      const response = await fetchItems();
      // Handle both direct arrays and objects with items property
      const itemsArray = Array.isArray(response) ? response : response.items || [];
      setItems(itemsArray);
    } catch (error) {
      console.error('Error loading items:', error);
      toast.error('Fehler beim Laden der Elemente');
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemToggle = item => {
    setSelectedItems(prev => {
      const isSelected = prev.some(i => i[itemIdKey] === item[itemIdKey]);

      if (singleSelect) {
        // For single select, just set the new item
        return isSelected ? [] : [item];
      } else {
        // For multi select, toggle the item
        if (isSelected) {
          return prev.filter(i => i[itemIdKey] !== item[itemIdKey]);
        } else {
          return [...prev, item];
        }
      }
    });
  };

  const handleAssign = () => {
    if (singleSelect) {
      // For single select, pass the single ID or null
      onAssign(selectedItems[0]?.[itemIdKey] || null);
    } else {
      // For multi select, pass array of IDs
      onAssign(selectedItems.map(item => item[itemIdKey]));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              {selectedItems.length} ausgewählt
            </Typography>
            <List>
              {items.map(item => (
                <ListItemButton
                  key={item[itemIdKey]}
                  selected={selectedItems.some(i => i[itemIdKey] === item[itemIdKey])}
                  onClick={() => handleItemToggle(item)}
                >
                  {Icon && (
                    <ListItemIcon>
                      <Icon />
                    </ListItemIcon>
                  )}
                  <ListItemText primary={item[itemNameKey]} />
                </ListItemButton>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button onClick={handleAssign} disabled={loading} variant='contained' color='primary'>
          {loading ? 'Wird zugewiesen...' : 'Zuweisen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BaseAssignmentModal;
